import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent,
} from "react";
import { openAfterKeyboardDismiss } from "@/shared/lib/openAfterKeyboardDismiss";
import { useTapInteraction } from "@/shared/ui/pressable";
import InputShell from "./InputShell";
import styles from "./ResidentIdInput.module.scss";

type ResidentIdInputProps = {
  label?: string;
  /** 앞 6자리 (숫자만, 외부에서 sanitize 되어 들어오는 것을 가정하나 내부에서도 한 번 더 정리한다). */
  frontValue: string;
  /** 뒷자리 입력 진척도 (실제 값은 외부 keypad 가 보관, 여기서는 dot 개수만 그린다). */
  backLength: number;
  onFrontChange: (value: string) => void;
  /** 앞자리 6자리 완료 시 또는 뒷자리 영역 클릭 시 호출. */
  onOpenKeypad: () => void;
  /**
   * 사용자가 실제 입력해야 하는 뒷자리 길이. 기본 7 (본인인증).
   * 1 로 지정하면 첫 dot 만 사용자 입력 슬롯이 되고 나머지 6개 dot 은 항상 마스킹으로 표시된다.
   * (Figma 168:17315 — 아이디 찾기: 주민번호 뒤 1자리만 입력)
   */
  requiredBackLength?: number;
  disabled?: boolean;
  tabIndex?: number;
  className?: string;
};

const FRONT_MAX = 6;
const BACK_MAX = 7;
const DEFAULT_REQUIRED_BACK = 7;

const ResidentIdInput = forwardRef<HTMLInputElement, ResidentIdInputProps>(
  function ResidentIdInput(
    {
      label = "주민등록번호",
      frontValue,
      backLength,
      onFrontChange,
      onOpenKeypad,
      requiredBackLength = DEFAULT_REQUIRED_BACK,
      disabled,
      tabIndex,
      className,
    },
    ref,
  ) {
    const requiredBack = Math.max(1, Math.min(BACK_MAX, requiredBackLength));
    const frontRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => frontRef.current as HTMLInputElement);

    const { isPressed: backPressed, ref: backRef, pressHandlers: backHandlers } = useTapInteraction<HTMLButtonElement>({ disabled });
    const [frontFocused, setFrontFocused] = useState(false);
    const filled = frontValue.length > 0 || backLength > 0;
    // empty + blur 상태에서는 다른 input 과 동일하게 label 만 vertically centered 로 표시한다.
    // sep 과 뒷자리 trigger 는 focus 또는 값이 채워졌을 때만 노출.
    const isCompact = frontFocused || filled;

    // 앞자리 완료 → 키패드 자동오픈 대기(openAfterKeyboardDismiss)를 취소하는 핸들.
    // 연속 수정(빠른 재입력)으로 대기가 중첩되지 않도록 새 요청 전 이전 것을 취소하고,
    // 언마운트 시에도 정리한다.
    const cancelAutoOpenRef = useRef<(() => void) | null>(null);
    useEffect(
      () => () => {
        cancelAutoOpenRef.current?.();
        cancelAutoOpenRef.current = null;
      },
      [],
    );

    const handleFrontChange = (e: ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value.replace(/[^0-9]/g, "").slice(0, FRONT_MAX);
      onFrontChange(next);
      // 앞자리 6자리 완료 시 자동으로 뒷자리 keypad 를 띄우되,
      // 뒷자리가 이미 requiredBack 만큼 완성된 상태(앞자리 수정 케이스)에서는 다시 띄우지 않는다.
      // native keyboard 가 다 내려간 뒤 keypad 를 열어야 겹치지 않는다 — openAfterKeyboardDismiss
      // 가 내부에서 현재 focus 된 frontRef 를 blur 하고 하강 정착(또는 fallback)을 기다린다.
      if (next.length === FRONT_MAX && backLength < requiredBack) {
        cancelAutoOpenRef.current?.();
        cancelAutoOpenRef.current = openAfterKeyboardDismiss(() => {
          cancelAutoOpenRef.current = null;
          onOpenKeypad();
        });
      }
    };

    const handleBackTriggerClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      // 앞자리 input 포커스가 남아 있으면 native keyboard 가 다 내려간 뒤 keypad 를 연다
      // (같은 tick 오픈 시 키보드와 겹침 — openAfterKeyboardDismiss 가 blur+대기 처리).
      openAfterKeyboardDismiss(onOpenKeypad);
    };

    // mousedown 단계에서 default focus 이동을 막아 앞자리 input focus 가 남아 있어도
    // click event 가 keyboard 닫힘 reflow 와 충돌해 누락되지 않도록 한다.
    const handleBackTriggerMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
    };

    return (
      <InputShell
        label={label}
        focused={frontFocused}
        filled={filled}
        disabled={disabled}
        className={className}
        onClick={() => frontRef.current?.focus()}
      >
        <div className={styles.row}>
          <input
            ref={frontRef}
            className={styles.frontInput}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={FRONT_MAX}
            placeholder={isCompact ? "앞 6자리" : ""}
            value={frontValue}
            onChange={handleFrontChange}
            onFocus={() => setFrontFocused(true)}
            onBlur={() => setFrontFocused(false)}
            aria-label="주민등록번호 앞자리"
            tabIndex={tabIndex}
            disabled={disabled}
            autoComplete="off"
          />
          {isCompact && (
            <>
              <span className={styles.sep} aria-hidden="true">
                -
              </span>
              <button
                ref={backRef}
                type="button"
                className={`${styles.backTrigger} ${backPressed ? styles.pressed : ""}`.trim()}
                onMouseDown={handleBackTriggerMouseDown}
                onClick={handleBackTriggerClick}
                disabled={disabled}
                aria-label="주민등록번호 뒷자리 입력"
                tabIndex={tabIndex}
                {...backHandlers}
              >
                <span className={styles.dots}>
                  {Array.from({ length: BACK_MAX }).map((_, i) => {
                    // i < requiredBack: 사용자 입력 슬롯 — 입력 진척도에 따라 filled
                    // i >= requiredBack: 항상 filled (마스킹된 것처럼 표시)
                    const isFilled =
                      i < requiredBack ? i < backLength : true;
                    return (
                      <span
                        key={i}
                        className={[
                          styles.dot,
                          isFilled ? styles.dotFilled : styles.dotEmpty,
                        ].join(" ")}
                        aria-hidden="true"
                      >
                        ●
                      </span>
                    );
                  })}
                </span>
              </button>
            </>
          )}
        </div>
      </InputShell>
    );
  },
);

export default ResidentIdInput;
export type { ResidentIdInputProps };
